/**
 * FJImageCropUtil - 图片裁剪相关工具方法
 */

class FJImageCropUtil {
    /**
     * 加载图片到 Canvas
     * @param {string} imageUrl - 图片URL
     * @returns {Promise<HTMLCanvasElement>} Canvas元素
     */
    static async loadImageToCanvas(imageUrl) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                resolve(canvas);
            };

            img.onerror = () => {
                reject(new Error('Failed to load image'));
            };

            img.src = imageUrl;
        });
    }

    /**
     * 计算图片在容器中的缩放比例和位置
     * @param {number} imgWidth - 图片原始宽度
     * @param {number} imgHeight - 图片原始高度
     * @param {number} containerWidth - 容器宽度
     * @param {number} containerHeight - 容器高度
     * @param {string} fitMode - 适配模式: 'contain' | 'cover'
     * @returns {Object} 缩放信息
     */
    static calculateImageScale(imgWidth, imgHeight, containerWidth, containerHeight, fitMode = 'contain') {
        const scaleX = containerWidth / imgWidth;
        const scaleY = containerHeight / imgHeight;

        let scale, displayWidth, displayHeight, offsetX, offsetY;

        if (fitMode === 'cover') {
            scale = Math.max(scaleX, scaleY);
            displayWidth = imgWidth * scale;
            displayHeight = imgHeight * scale;
            offsetX = (containerWidth - displayWidth) / 2;
            offsetY = (containerHeight - displayHeight) / 2;
        } else {
            scale = Math.min(scaleX, scaleY);
            displayWidth = imgWidth * scale;
            displayHeight = imgHeight * scale;
            offsetX = (containerWidth - displayWidth) / 2;
            offsetY = (containerHeight - displayHeight) / 2;
        }

        return {
            scale,
            displayWidth,
            displayHeight,
            offsetX,
            offsetY,
            originalWidth: imgWidth,
            originalHeight: imgHeight,
        };
    }

    /**
     * 将屏幕坐标转换为图片原始坐标
     * @param {Object} screenPos - 屏幕坐标 {x, y}
     * @param {Object} scaleInfo - 缩放信息
     * @returns {Object} 图片原始坐标
     */
    static screenToImageCoords(screenPos, scaleInfo) {
        const { scale, offsetX, offsetY } = scaleInfo;

        return {
            x: (screenPos.x - offsetX) / scale,
            y: (screenPos.y - offsetY) / scale,
        };
    }

    /**
     * 将图片原始坐标转换为屏幕坐标
     * @param {Object} imagePos - 图片原始坐标 {x, y}
     * @param {Object} scaleInfo - 缩放信息
     * @returns {Object} 屏幕坐标
     */
    static imageToScreenCoords(imagePos, scaleInfo) {
        const { scale, offsetX, offsetY } = scaleInfo;

        return {
            x: imagePos.x * scale + offsetX,
            y: imagePos.y * scale + offsetY,
        };
    }
    /**
     * 裁剪并导出图片（支持 maxSize）
     * @param {HTMLCanvasElement} sourceCanvas - 原始绘制好的 canvas
     * @param {{x: number, y: number, width: number, height: number}} cropRect - 裁剪矩形
     * @param {Object} options - 配置参数
     * @param {string} [options.type="image/jpeg"] - 输出格式 ("image/jpeg", "image/png", "image/webp")
     * @param {number} [options.quality=0.9] - 初始压缩质量 (仅 JPEG/WebP 有效)
     * @param {boolean} [options.asFile=false] - 是否返回 File (默认返回 DataURL)
     * @param {string} [options.fileName="cropped.jpg"] - 如果返回 File，设置文件名
     * @param {number} [options.maxSize] - 允许的最大文件大小 (单位: 字节)
     * @returns {Promise<string|File>} - DataURL 或 File
     */
    static async exportCropImage(sourceCanvas, cropRect, options = {}) {
        const { fileName = 'cropped.png', maxSize, cropRatio } = options;

        const targetCanvas = document.createElement('canvas');
        targetCanvas.width = cropRect.width;
        targetCanvas.height = cropRect.height;

        const ctx = targetCanvas.getContext('2d');

        // 裁剪绘制
        ctx.drawImage(
            sourceCanvas,
            cropRect.x,
            cropRect.y,
            cropRect.width,
            cropRect.height,
            0,
            0,
            cropRect.width,
            cropRect.height,
        );

        function canvasToBlob(cvs) {
            return new Promise(resolve => {
                cvs.toBlob(blob => resolve(blob), 'image/png');
            });
        }

        let blob = await canvasToBlob(targetCanvas);

        if (blob.size > maxSize) {
            let scale = Math.sqrt(maxSize / blob.size);
            // PNG不可调质量，只能调尺寸
            let newWidth = Math.floor(targetCanvas.width * scale);
            let newHeight = Math.floor(targetCanvas.height * scale);

            let tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = newWidth;
            tmpCanvas.height = newHeight;

            let ctx = tmpCanvas.getContext('2d');
            ctx.drawImage(targetCanvas, 0, 0, newWidth, newHeight);

            blob = await canvasToBlob(tmpCanvas);

            // 保险起见，多次缩放直到满足 maxSize
            while (blob.size > maxSize && newWidth > 50 && newHeight > 50) {
                scale = 0.9; // 再缩小 90%
                newWidth = Math.floor(newWidth * scale);
                newHeight = Math.floor(newHeight * scale);

                tmpCanvas.width = newWidth;
                tmpCanvas.height = newHeight;
                ctx.drawImage(targetCanvas, 0, 0, newWidth, newHeight);
                blob = await canvasToBlob(tmpCanvas);
            }
        }
        return {
            file: new File([blob], fileName, { type: 'image/png' }),
            width: cropRect.width,
            height: cropRect.height,
            cropRect,
            cropRatio,
        };
    }

    /**
     * 验证裁剪区域是否有效
     * @param {Object} cropRect - 裁剪区域
     * @param {Object} imageSize - 图片尺寸
     * @returns {boolean} 是否有效
     */
    static validateCropRect(cropRect, imageSize) {
        const { x, y, width, height } = cropRect;
        const { originalWidth, originalHeight } = imageSize;

        return (
            x >= 0 && y >= 0 && x + width <= originalWidth && y + height <= originalHeight && width > 0 && height > 0
        );
    }

    /**
     * 限制裁剪区域在图片范围内
     * @param {Object} cropRect - 裁剪区域
     * @param {Object} imageSize - 图片尺寸
     * @param {Object} constraints - 尺寸限制
     * @returns {Object} 限制后的裁剪区域
     */
    static constrainCropRect(cropRect, imageSize, constraints = {}) {
        const { x, y, width, height } = cropRect;
        const { originalWidth, originalHeight } = imageSize;
        let { minWidth = 1, minHeight = 1, maxWidth = originalWidth, maxHeight = originalHeight, ratio } = constraints;
        const originRatio = originalWidth / originalHeight;
        if (ratio !== undefined && ratio > 0) {
            if (ratio > originRatio) {
                maxHeight = Math.min(maxHeight, maxWidth / ratio);
            } else {
                maxWidth = Math.min(maxWidth, maxHeight * ratio);
            }
        }

        // 首先应用最小和最大尺寸限制
        let constrainedWidth = Math.max(minWidth, Math.min(width, maxWidth));
        let constrainedHeight = Math.max(minHeight, Math.min(height, maxHeight));

        // 如果尺寸被限制，需要调整坐标以保持矩形在图片范围内
        let adjustedX = x;
        let adjustedY = y;

        // 如果宽度被限制到最小值，调整X坐标
        if (constrainedWidth === minWidth && width < minWidth) {
            adjustedX = Math.max(0, Math.min(x, originalWidth - minWidth));
        }
        // 如果宽度被限制到最大值，调整X坐标
        else if (constrainedWidth === maxWidth && width > maxWidth) {
            adjustedX = Math.max(0, Math.min(x, originalWidth - maxWidth));
        }
        // 如果宽度超出图片范围，调整X坐标
        else if (adjustedX + constrainedWidth > originalWidth) {
            adjustedX = originalWidth - constrainedWidth;
        }

        // 如果高度被限制到最小值，调整Y坐标
        if (constrainedHeight === minHeight && height < minHeight) {
            adjustedY = Math.max(0, Math.min(y, originalHeight - minHeight));
        }
        // 如果高度被限制到最大值，调整Y坐标
        else if (constrainedHeight === maxHeight && height > maxHeight) {
            adjustedY = Math.max(0, Math.min(y, originalHeight - maxHeight));
        }
        // 如果高度超出图片范围，调整Y坐标
        else if (adjustedY + constrainedHeight > originalHeight) {
            adjustedY = originalHeight - constrainedHeight;
        }

        // 最终确保坐标在有效范围内
        const finalX = Math.max(0, Math.min(adjustedX, originalWidth - constrainedWidth));
        const finalY = Math.max(0, Math.min(adjustedY, originalHeight - constrainedHeight));

        return {
            x: finalX,
            y: finalY,
            width: constrainedWidth,
            height: constrainedHeight,
        };
    }

    /**
     * 将画布像素约束转换为实际像素约束
     * @param {Object} constraints - 画布像素约束
     * @param {Object} scaleInfo - 缩放信息
     * @returns {Object} 实际像素约束
     */
    static mapConstraintsToActualPixels(constraints, scaleInfo) {
        if (!scaleInfo || !scaleInfo.scale) {
            return constraints;
        }

        const { minWidth = 1, minHeight = 1, maxWidth, maxHeight } = constraints;

        return {
            minWidth: minWidth / scaleInfo.scale,
            minHeight: minHeight / scaleInfo.scale,
            maxWidth: maxWidth ? maxWidth / scaleInfo.scale : undefined,
            maxHeight: maxHeight ? maxHeight / scaleInfo.scale : undefined,
        };
    }

    static getCropHandles(screenCropRect, handleSize, verticalWidth, horizontalWidth) {
        return [
            {
                x: screenCropRect.x - handleSize / 2,
                y: screenCropRect.y - handleSize / 2,
                type: 'nw',
                width: handleSize,
                height: handleSize,
            }, // 左上
            {
                x: screenCropRect.x + screenCropRect.width - handleSize / 2,
                y: screenCropRect.y - handleSize / 2,
                type: 'ne',
                width: handleSize,
                height: handleSize,
            }, // 右上
            {
                x: screenCropRect.x - handleSize / 2,
                y: screenCropRect.y + screenCropRect.height - handleSize / 2,
                type: 'sw',
                width: handleSize,
                height: handleSize,
            }, // 左下
            {
                x: screenCropRect.x + screenCropRect.width - handleSize / 2,
                y: screenCropRect.y + screenCropRect.height - handleSize / 2,
                type: 'se',
                width: handleSize,
                height: handleSize,
            }, // 右下
            {
                x: screenCropRect.x + screenCropRect.width / 2 - horizontalWidth / 2,
                y: screenCropRect.y - handleSize / 2 + verticalWidth / 2,
                type: 'n',
                width: horizontalWidth,
                height: verticalWidth,
            }, // 上
            {
                x: screenCropRect.x + screenCropRect.width / 2 - horizontalWidth / 2,
                y: screenCropRect.y + screenCropRect.height - verticalWidth / 2,
                type: 's',
                width: horizontalWidth,
                height: verticalWidth,
            }, // 下
            {
                x: screenCropRect.x - verticalWidth / 2,
                y: screenCropRect.y + screenCropRect.height / 2 - horizontalWidth / 2,
                type: 'w',
                width: verticalWidth,
                height: horizontalWidth,
            }, // 左
            {
                x: screenCropRect.x + screenCropRect.width - verticalWidth / 2,
                y: screenCropRect.y + screenCropRect.height / 2 - horizontalWidth / 2,
                type: 'e',
                width: verticalWidth,
                height: horizontalWidth,
            }, // 右
        ];
    }

    static getIsOnHandles(x, y, options) {
        const { screenCropRect, handleSize, verticalWidth, horizontalWidth } = options;
        const handles = this.getCropHandles(screenCropRect, handleSize, verticalWidth, horizontalWidth);
        const type = handles.find(handle => {
            if (handle.type === 'nw' || handle.type === 'ne' || handle.type === 'sw' || handle.type === 'se') {
                return x >= handle.x && x <= handle.x + handle.width && y >= handle.y && y <= handle.y + handle.height;
            } else if (handle.type === 'n' || handle.type === 's' || handle.type === 'w' || handle.type === 'e') {
                return x >= handle.x && x <= handle.x + handle.width && y >= handle.y && y <= handle.y + handle.height;
            }
        });
        return type;
    }

    /**
     * 绘制九宫格辅助线
     * @param {CanvasRenderingContext2D} ctx - Canvas 2D 上下文
     * @param {Object} screenCropRect - 屏幕坐标下的裁剪矩形
     * @param {Object} options - 绘制选项
     */
    static drawRuleOfThirdsGrid(ctx, screenCropRect, options = {}) {
        const { lineWidth = 1, lineColor = 'rgba(255, 255, 255, 0.8)' } = options;

        ctx.save();
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineWidth;
        ctx.setLineDash([]);

        const { x, y, width, height } = screenCropRect;

        // 计算九宫格分割点
        const thirdWidth = width / 3;
        const thirdHeight = height / 3;

        // 绘制两条垂直线
        ctx.beginPath();
        ctx.moveTo(x + thirdWidth, y);
        ctx.lineTo(x + thirdWidth, y + height);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(x + thirdWidth * 2, y);
        ctx.lineTo(x + thirdWidth * 2, y + height);
        ctx.stroke();

        // 绘制两条水平线
        ctx.beginPath();
        ctx.moveTo(x, y + thirdHeight);
        ctx.lineTo(x + width, y + thirdHeight);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(x, y + thirdHeight * 2);
        ctx.lineTo(x + width, y + thirdHeight * 2);
        ctx.stroke();

        ctx.restore();
    }

    /**
     * 调整裁剪区域大小
     * @param {Object} cropRect - 当前裁剪区域
     * @param {string} resizeHandle - 调整手柄类型
     * @param {number} deltaX - X轴移动距离
     * @param {number} deltaY - Y轴移动距离
     * @param {number} scale - 缩放比例
     * @param {Object} constraints - 尺寸限制
     * @param {number} [constraints.minWidth=1] - 最小宽度
     * @param {number} [constraints.minHeight=1] - 最小高度
     * @param {number} [constraints.maxWidth] - 最大宽度
     * @param {number} [constraints.maxHeight] - 最大高度
     * @param {number} [constraints.ratio] - 宽高比例，undefined为自由模式
     * @param {Object} imageSize - 图片尺寸
     * @param {number} imageSize.width - 图片宽度
     * @param {number} imageSize.height - 图片高度
     * @returns {Object} 调整后的裁剪区域
     */
    static resizeCropRect(cropRect, resizeHandle, deltaX, deltaY, scale, constraints, imageSize) {
        let { minWidth = 1, minHeight = 1, ratio = undefined } = constraints;
        if (ratio !== undefined && ratio > 0) {
            if (ratio > 1) {
                minWidth = Math.max(minWidth, minHeight * ratio);
            } else {
                minHeight = Math.max(minHeight, minWidth / ratio);
            }
        }
        let newCropRect = { ...cropRect };

        switch (resizeHandle) {
            case 'nw': {
                // 计算新的尺寸
                const newWidthNW = Math.max(minWidth, cropRect.width - deltaX / scale);
                const newHeightNW = Math.max(minHeight, cropRect.height - deltaY / scale);

                // 计算实际尺寸变化
                const widthDeltaNW = newWidthNW - cropRect.width;
                const heightDeltaNW = newHeightNW - cropRect.height;

                // 更新尺寸和位置
                newCropRect.width = newWidthNW;
                newCropRect.height = newHeightNW;
                newCropRect.x = cropRect.x - widthDeltaNW;
                newCropRect.y = cropRect.y - heightDeltaNW;
                break;
            }
            case 'ne': {
                // 计算新的尺寸
                const newWidthNE = Math.max(minWidth, cropRect.width + deltaX / scale);
                const newHeightNE = Math.max(minHeight, cropRect.height - deltaY / scale);

                // 计算实际尺寸变化
                const heightDeltaNE = newHeightNE - cropRect.height;

                // 更新尺寸和位置
                newCropRect.width = newWidthNE;
                newCropRect.height = newHeightNE;
                newCropRect.y = cropRect.y - heightDeltaNE;
                break;
            }
            case 'sw': {
                // 计算新的尺寸
                const newWidthSW = Math.max(minWidth, cropRect.width - deltaX / scale);
                const newHeightSW = Math.max(minHeight, cropRect.height + deltaY / scale);

                // 计算实际尺寸变化
                const widthDeltaSW = newWidthSW - cropRect.width;

                // 更新尺寸和位置
                newCropRect.width = newWidthSW;
                newCropRect.height = newHeightSW;
                newCropRect.x = cropRect.x - widthDeltaSW;
                break;
            }
            case 'se':
                newCropRect.width = Math.max(minWidth, cropRect.width + deltaX / scale);
                newCropRect.height = Math.max(minHeight, cropRect.height + deltaY / scale);
                break;
            case 'n': {
                // 计算新的高度
                const newHeightN = Math.max(minHeight, cropRect.height - deltaY / scale);

                // 计算实际高度变化
                const heightDeltaN = newHeightN - cropRect.height;

                // 更新高度和位置
                newCropRect.height = newHeightN;
                newCropRect.y = cropRect.y - heightDeltaN;
                break;
            }
            case 's':
                newCropRect.height = Math.max(minHeight, cropRect.height + deltaY / scale);
                break;
            case 'w': {
                // 计算新的宽度
                const newWidthW = Math.max(minWidth, cropRect.width - deltaX / scale);

                // 计算实际宽度变化
                const widthDeltaW = newWidthW - cropRect.width;

                // 更新宽度和位置
                newCropRect.width = newWidthW;
                newCropRect.x = cropRect.x - widthDeltaW;
                break;
            }
            case 'e':
                newCropRect.width = Math.max(minWidth, cropRect.width + deltaX / scale);
                break;
        }

        // 检查边界，防止位置超出图片范围时继续增加大小
        if (imageSize) {
            const { width: imageWidth, height: imageHeight } = imageSize;

            // 如果裁剪区域已经超出图片边界，限制继续增加大小
            if (newCropRect.x < 0) {
                // 左边界超出，限制向左扩展
                if (resizeHandle === 'nw' || resizeHandle === 'w') {
                    newCropRect.width = Math.max(minWidth, newCropRect.width + newCropRect.x);
                    newCropRect.x = 0;
                } else if (resizeHandle === 'sw') {
                    newCropRect.width = Math.max(minWidth, newCropRect.width + newCropRect.x);
                    newCropRect.x = 0;
                }
            }

            if (newCropRect.y < 0) {
                // 上边界超出，限制向上扩展
                if (resizeHandle === 'nw' || resizeHandle === 'n') {
                    newCropRect.height = Math.max(minHeight, newCropRect.height + newCropRect.y);
                    newCropRect.y = 0;
                } else if (resizeHandle === 'ne') {
                    newCropRect.height = Math.max(minHeight, newCropRect.height + newCropRect.y);
                    newCropRect.y = 0;
                }
            }

            if (newCropRect.x + newCropRect.width > imageWidth) {
                // 右边界超出，限制向右扩展
                if (resizeHandle === 'ne' || resizeHandle === 'e') {
                    newCropRect.width = Math.max(minWidth, imageWidth - newCropRect.x);
                } else if (resizeHandle === 'se') {
                    newCropRect.width = Math.max(minWidth, imageWidth - newCropRect.x);
                }
            }

            if (newCropRect.y + newCropRect.height > imageHeight) {
                // 下边界超出，限制向下扩展
                if (resizeHandle === 'sw' || resizeHandle === 's') {
                    newCropRect.height = Math.max(minHeight, imageHeight - newCropRect.y);
                } else if (resizeHandle === 'se') {
                    newCropRect.height = Math.max(minHeight, imageHeight - newCropRect.y);
                }
            }
        }

        // 如果指定了比例，根据实际变化的维度调整另一个维度
        if (ratio !== undefined && ratio > 0) {
            const widthChanged = Math.abs(newCropRect.width - cropRect.width) > 0.001;
            const heightChanged = Math.abs(newCropRect.height - cropRect.height) > 0.001;

            if (widthChanged && !heightChanged) {
                // 宽度变化，按比例调整高度，按中心变化
                const oldHeight = newCropRect.height;
                const newHeight = newCropRect.width / ratio;

                // 检查是否达到最小高度限制
                if (newHeight >= minHeight) {
                    newCropRect.height = newHeight;
                    const heightDelta = newCropRect.height - oldHeight;
                    newCropRect.y -= heightDelta / 2;
                } else {
                    // 达到最小高度限制，不调整高度和位置
                    newCropRect.height = minHeight;
                }
            } else if (heightChanged && !widthChanged) {
                // 高度变化，按比例调整宽度，按中心变化
                const oldWidth = newCropRect.width;
                const newWidth = newCropRect.height * ratio;

                // 检查是否达到最小宽度限制
                if (newWidth >= minWidth) {
                    newCropRect.width = newWidth;
                    const widthDelta = newCropRect.width - oldWidth;
                    newCropRect.x -= widthDelta / 2;
                } else {
                    // 达到最小宽度限制，不调整宽度和位置
                    newCropRect.width = minWidth;
                }
            } else if (widthChanged && heightChanged) {
                // 两个维度都变化（如角落调整），保持目标比例
                const currentRatio = newCropRect.width / newCropRect.height;
                if (Math.abs(currentRatio - ratio) > 0.001) {
                    // 根据变化更大的维度来决定调整策略
                    const widthChangeRatio = newCropRect.width / cropRect.width;
                    const heightChangeRatio = newCropRect.height / cropRect.height;

                    if (Math.abs(widthChangeRatio - 1) > Math.abs(heightChangeRatio - 1)) {
                        // 宽度变化更大，以宽度为准调整高度，按中心变化
                        const oldHeight = newCropRect.height;
                        const newHeight = newCropRect.width / ratio;

                        // 检查是否达到最小高度限制
                        if (newHeight >= minHeight) {
                            newCropRect.height = newHeight;
                            const heightDelta = newCropRect.height - oldHeight;
                            newCropRect.y -= heightDelta / 2;
                        } else {
                            // 达到最小高度限制，不调整高度和位置
                            newCropRect.height = minHeight;
                        }
                    } else {
                        // 高度变化更大，以高度为准调整宽度，按中心变化
                        const oldWidth = newCropRect.width;
                        const newWidth = newCropRect.height * ratio;

                        // 检查是否达到最小宽度限制
                        if (newWidth >= minWidth) {
                            newCropRect.width = newWidth;
                            const widthDelta = newCropRect.width - oldWidth;
                            newCropRect.x -= widthDelta / 2;
                        } else {
                            // 达到最小宽度限制，不调整宽度和位置
                            newCropRect.width = minWidth;
                        }
                    }
                }
            }
        }

        return newCropRect;
    }
}

export default FJImageCropUtil;
